import React, { useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import { getRegions } from '@luxuryescapes/lib-regions';

import { APP_EXCLUSIVE_BRAND, APP_EXCLUSIVE_TIMEZONE } from '~/consts/appExclusive';

import { useGetAppExclusiveSchedules } from '~/hooks/apis/offers/useGetAppExclusiveSchedules';
import { useGetOffersByIds } from '~/hooks/apis/offers/useGetOffersByIds';

import type { ScheduleRecord } from '~/services/OffersService';

import AddAppOnlyFlashDealDialog from './AddAppOnlyFlashDealDialog';
import AppOnlyFlashCalendarDay from './AppOnlyFlashCalendarDay';
import CloneAppOnlyFlashScheduleDialog from './CloneAppOnlyFlashScheduleDialog';
import MoveAppOnlyFlashDealDialog from './MoveAppOnlyFlashDealDialog';
import {
  buildCalendarDays,
  currentMonth,
  defaultAddDealDate,
  formatDayLabel,
  formatMonthLabel,
  getCalendarQueryRange,
  getUpcomingEmptyDays,
  getUpcomingQueryRange,
  groupSchedulesByDate,
  shiftMonth,
  todayDate,
} from './appOnlyFlashCalendar';

const DEFAULT_REGION = 'AU';
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function AppOnlyFlashPage() {
  const regions = useMemo(() => getRegions(APP_EXCLUSIVE_BRAND), []);
  const [region, setRegion] = useState(DEFAULT_REGION);
  const [month, setMonth] = useState(() => currentMonth());
  // Sydney day the Add deal dialog is open on; null while it is closed.
  const [addDealDate, setAddDealDate] = useState<string | null>(null);
  // Row the Move dialog is open on; null while it is closed.
  const [movingSchedule, setMovingSchedule] = useState<ScheduleRecord | null>(null);
  const [isCloneOpen, setIsCloneOpen] = useState(false);
  const regionName = regions.find((item) => item.code === region)?.name ?? region;

  const today = todayDate();
  const days = useMemo(() => buildCalendarDays(month), [month]);
  const range = useMemo(() => getCalendarQueryRange(days), [days]);

  const { data: schedules = [], isLoading, isError, error } = useGetAppExclusiveSchedules({ region, ...range });
  const schedulesByDate = useMemo(() => groupSchedulesByDate(schedules, days), [schedules, days]);
  const offerIds = useMemo(() => schedules.map((schedule) => schedule.fk_offer_salesforce_id), [schedules]);
  const { offersById, loadingOfferIds } = useGetOffersByIds(offerIds);

  // Tomorrow's deal is on the homepage all day today, so warn while either of the next two days is empty.
  const upcomingRange = useMemo(() => getUpcomingQueryRange(today), [today]);
  const { data: upcomingSchedules } = useGetAppExclusiveSchedules({ region, ...upcomingRange });
  const upcomingEmptyDays = upcomingSchedules ? getUpcomingEmptyDays(upcomingSchedules, today) : [];

  const hasSchedulesThisMonth = days.some((day) => day.inMonth && schedulesByDate.has(day.date));

  return (
    <Container maxWidth="xl" sx={{ pt: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" component="h2">
            App Only Flash
          </Typography>
          <Typography variant="body2" color="text.secondary">
            One offline Flash deal per day, per region. Each slot runs from 6:00am Sydney time.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <FormControl sx={{ minWidth: 240 }}>
              <InputLabel id="app-only-flash-region-label">Region</InputLabel>
              <Select
                labelId="app-only-flash-region-label"
                label="Region"
                value={region}
                onChange={(event) => setRegion(event.target.value)}
              >
                {regions.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.name} ({item.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Times shown in {APP_EXCLUSIVE_TIMEZONE}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <Button variant="text" startIcon={<ContentCopyIcon />} onClick={() => setIsCloneOpen(true)}>
              Copy schedule
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDealDate(defaultAddDealDate())}>
              Add deal
            </Button>
          </Stack>
        </Stack>

        {upcomingEmptyDays.length > 0 && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => setAddDealDate(upcomingEmptyDays[0])}>
                Add deal
              </Button>
            }
          >
            Nothing scheduled in {regionName} for {upcomingEmptyDays.map(formatDayLabel).join(' or ')}. Tomorrow's deal
            shows on the homepage all day today.
          </Alert>
        )}
        {isError && <Alert severity="error">Could not load schedules: {error?.message}</Alert>}
        {!isLoading && !isError && !hasSchedulesThisMonth && (
          <Alert severity="info">No App Only Flash deals scheduled in {formatMonthLabel(month)}.</Alert>
        )}

        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Typography variant="h6" component="h3" fontWeight={400} sx={{ minWidth: 170 }}>
              {formatMonthLabel(month)}
            </Typography>
            <IconButton aria-label="Previous month" onClick={() => setMonth((value) => shiftMonth(value, -1))}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton aria-label="Next month" onClick={() => setMonth((value) => shiftMonth(value, 1))}>
              <ChevronRightIcon />
            </IconButton>
            <Button variant="text" size="small" onClick={() => setMonth(currentMonth())}>
              Today
            </Button>
            {isLoading && <CircularProgress size={18} />}
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              bgcolor: 'grey.50',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {WEEKDAYS.map((weekday) => (
              <Typography
                key={weekday}
                variant="caption"
                color="text.secondary"
                fontWeight={500}
                sx={{ px: 1.5, py: 1 }}
              >
                {weekday}
              </Typography>
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
            {days.map((day) => (
              <AppOnlyFlashCalendarDay
                key={day.date}
                day={day}
                isToday={day.date === today}
                isPast={day.date < today}
                schedules={schedulesByDate.get(day.date) ?? []}
                offersById={offersById}
                loadingOfferIds={loadingOfferIds}
                onAddDeal={() => setAddDealDate(day.date)}
                onMoveDeal={setMovingSchedule}
              />
            ))}
          </Box>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
            <InfoOutlinedIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              One deal per day per region · slots run 6:00am to 6:00am Sydney time · click a deal to open the offer, +
              on an empty day to add one, or the move icon on an upcoming deal to move or swap it
            </Typography>
          </Stack>
        </Paper>
      </Stack>

      <AddAppOnlyFlashDealDialog
        open={addDealDate !== null}
        region={region}
        regionName={regionName}
        initialDate={addDealDate ?? defaultAddDealDate()}
        onClose={() => setAddDealDate(null)}
      />
      <MoveAppOnlyFlashDealDialog
        schedule={movingSchedule}
        regionName={regionName}
        getOfferName={(offerId) => offersById.get(offerId)?.name}
        onClose={() => setMovingSchedule(null)}
      />
      <CloneAppOnlyFlashScheduleDialog
        open={isCloneOpen}
        sourceRegion={region}
        regions={regions}
        month={month}
        onClose={() => setIsCloneOpen(false)}
      />
    </Container>
  );
}
